import { useEffect, useState } from 'react'

export type RunStreamEvent =
  | { kind: 'started';   siteId: string; runId: string; ts: string }
  | { kind: 'crawled';   runId: string; pagesCrawled: number; ts: string }
  | { kind: 'evaluated'; runId: string; rulesPassed: number; rulesFailed: number; ts: string }
  | { kind: 'probed';    runId: string; engineId: string; query: string; cited: boolean; ts: string }
  | { kind: 'fix';       runId: string; fixId: string; ruleId: string; status: string; ts: string }
  | { kind: 'completed'; runId: string; score: number | null; ts: string }
  | { kind: 'failed';    runId: string; error: string; ts: string }
  | { kind: 'hello';     runId: string; status: string; score: number | null }

const API_BASE = import.meta.env.VITE_API_URL ?? '/v1'

/**
 * Subscribe to a run's SSE stream. Uses fetch + ReadableStream so we can pass
 * the JWT in Authorization header (EventSource cannot set headers).
 */
export function useRunStream(siteId: string, runId: string | null, onEvent: (e: RunStreamEvent) => void) {
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!runId) return
    const ctl = new AbortController()
    const jwt = localStorage.getItem('discover.jwt')

    ;(async () => {
      try {
        const res = await fetch(`${API_BASE}/sites/${siteId}/runs/${runId}/stream`, {
          headers: jwt ? { authorization: `Bearer ${jwt}` } : undefined,
          signal: ctl.signal,
        })
        if (!res.ok || !res.body) {
          setError(`stream failed: ${res.status}`)
          return
        }
        setConnected(true)
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          let idx: number
          while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const raw = buffer.slice(0, idx)
            buffer = buffer.slice(idx + 2)
            const evt = parseSseEvent(raw)
            if (evt) onEvent(evt)
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') setError((err as Error).message)
      } finally {
        setConnected(false)
      }
    })()

    return () => ctl.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, runId])

  return { connected, error }
}

function parseSseEvent(raw: string): RunStreamEvent | null {
  let data = ''
  for (const line of raw.split('\n')) {
    if (line.startsWith('data:')) data += line.slice(5).trimStart()
  }
  if (!data) return null
  try {
    return JSON.parse(data) as RunStreamEvent
  } catch {
    return null
  }
}
